import { For, Show } from "solid-js";
import type { MapTargetLane } from "../../enums/map";
import { MAP_TARGET_LANE } from "../../enums/map";
import { mapStatusLabel } from "./mapLabels";
import type { MapTargetItem, MapVocabulary } from "./mapTypes";

const LANES: MapTargetLane[] = [
  MAP_TARGET_LANE.ORGANISE,
  MAP_TARGET_LANE.PATTERN,
  MAP_TARGET_LANE.DESTINATION,
];

export function MapTargetEstate(props: {
  items: MapTargetItem[];
  vocabulary: MapVocabulary;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const laneItems = (lane: MapTargetLane) =>
    props.items.filter((item) => item.lane === lane);
  return (
    <section class="map-target-estate" aria-labelledby="map-target-title">
      <header class="map-section-head">
        <div>
          <span class="signal-mini-kicker">Draft target estate</span>
          <h2 id="map-target-title">Target content map</h2>
        </div>
        <span>Groups, not every page</span>
      </header>
      <Show
        when={props.items.length > 0}
        fallback={
          <div class="map-plan-empty">
            <strong>No supported target structure yet</strong>
            <p>
              Current pages remain available in Scan while more structural
              evidence or client context is gathered.
            </p>
          </div>
        }
      >
        <div class="map-target-lanes">
          <For each={LANES}>
            {(lane, index) => (
              <section class={`map-target-lane is-${lane}`}>
                <header>
                  <span>0{index() + 1}</span>
                  <h3>{laneLabel(lane, props.vocabulary)}</h3>
                </header>
                <div class="map-target-items">
                  <For each={laneItems(lane)}>
                    {(item) => (
                      <TargetCard
                        item={item}
                        selected={item.changeId === props.selectedId}
                        onSelect={props.onSelect}
                      />
                    )}
                  </For>
                  <Show when={laneItems(lane).length === 0}>
                    <p class="map-target-lane-empty">
                      No supported target item
                    </p>
                  </Show>
                </div>
              </section>
            )}
          </For>
        </div>
      </Show>
      <div class="map-target-legend">
        <span>
          <i class="is-current" /> Current group
        </span>
        <span>
          <i class="is-pending" /> Draft
        </span>
        <span>
          <i class="is-accepted" /> Accepted in preview
        </span>
        <span>
          <i class="is-unresolved" /> Needs context
        </span>
      </div>
    </section>
  );
}

function TargetCard(props: {
  item: MapTargetItem;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const body = () => (
    <>
      <span class="map-target-state">
        {mapStatusLabel(props.item.state, props.item.adjusted)}
      </span>
      <strong>{props.item.label}</strong>
      <p>{props.item.detail}</p>
      <Show when={props.item.relationship} keyed>
        {(relationship) => <small>{relationship}</small>}
      </Show>
      <span class="map-target-pages">
        {props.item.pageCount} represented page
        {props.item.pageCount === 1 ? "" : "s"}
      </span>
    </>
  );
  return (
    <Show
      when={props.item.changeId}
      keyed
      fallback={
        <article class={`map-target-card is-${props.item.state}`}>
          {body()}
        </article>
      }
    >
      {(changeId) => (
        <button
          type="button"
          class={`map-target-card is-${props.item.state}`}
          classList={{ "is-selected": props.selected }}
          aria-pressed={props.selected}
          aria-label={`${props.item.label}. ${mapStatusLabel(props.item.state, props.item.adjusted)}. ${props.item.pageCount} represented page${props.item.pageCount === 1 ? "" : "s"}.`}
          onClick={() => props.onSelect(changeId)}
        >
          {body()}
        </button>
      )}
    </Show>
  );
}

function laneLabel(lane: MapTargetLane, vocabulary: MapVocabulary): string {
  if (lane === MAP_TARGET_LANE.ORGANISE) return vocabulary.organise;
  if (lane === MAP_TARGET_LANE.PATTERN) return vocabulary.pattern;
  return vocabulary.destination;
}
